'use client';

import React from 'react';
import { useId, type ReactNode } from 'react';

import { Label } from '../../primitives/label';
import { RadioGroup, RadioGroupItem } from '../../primitives/radio-group';
import { cn } from '../../utils/cn';

export interface UzRadioOption<TValue extends string = string> {
  value: TValue;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface UzRadioGroupProps<TValue extends string = string> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  options: ReadonlyArray<UzRadioOption<TValue>>;
  value?: TValue;
  defaultValue?: TValue;
  onValueChange?: (value: TValue) => void;
  orientation?: 'vertical' | 'horizontal';
  disabled?: boolean;
  name?: string;
  containerClassName?: string;
}

export const UzRadioGroup = <TValue extends string = string>({
  label,
  hint,
  error,
  required,
  options,
  value,
  defaultValue,
  onValueChange,
  orientation = 'vertical',
  disabled,
  name,
  containerClassName,
}: UzRadioGroupProps<TValue>): React.JSX.Element => {
  const groupId = useId();

  return (
    <div
      className={cn('space-y-2', containerClassName)}
      role="group"
      aria-labelledby={label ? `${groupId}-label` : undefined}
    >
      {label && (
        <Label id={`${groupId}-label`} className={cn('block', disabled && 'opacity-60')}>
          {label}
          {required && (
            <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
              *
            </span>
          )}
        </Label>
      )}
      <RadioGroup
        value={value}
        defaultValue={defaultValue}
        onValueChange={(v) => onValueChange?.(v as TValue)}
        disabled={disabled}
        name={name}
        className={cn(orientation === 'horizontal' && 'flex flex-row gap-6')}
      >
        {options.map((opt) => {
          const itemId = `${groupId}-${opt.value}`;
          return (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem
                value={opt.value}
                id={itemId}
                disabled={opt.disabled}
                className="mt-0.5"
              />
              <div className="grid gap-0.5 leading-none">
                <label htmlFor={itemId} className="cursor-pointer text-sm leading-none font-medium">
                  {opt.label}
                </label>
                {opt.description && (
                  <p className="text-muted-foreground text-xs">{opt.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </RadioGroup>
      {error ? (
        <p className="text-destructive text-xs font-medium">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
};
